import React, { useEffect, useState, useMemo, useRef } from "react";
import { Controller, useFieldArray } from "react-hook-form";
import Addicon from "@mui/icons-material/Add";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button as UIButton,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Box,
} from "@mui/material";
import DeleteBin2LineIcon from "remixicon-react/DeleteBin2LineIcon";
import { ExpandMoreRounded } from "@mui/icons-material";
import clsx from "clsx";
import { Button, Paper, Text } from "$components";
import ProductSearchInput from "$forms/ProductSearchInput";
import useFilters from "$hooks/useFilters";
import useOrder from "$hooks/useOrder";
import { Dockyard } from "$types";
import ProductsInSubordersForm from "./ProductsInSubordersForm";
import useAuth from "$hooks/useAuth";
import DockyardSelect from "$forms/DockyardSelect";
import ProjectSelect from "$forms/ProjectSelect";
import useJStyles from "$hooks/useJStyles";
import { ButtonsList } from "./ButtonsList";
import { checkSuborderEdbs } from "$services/EDB.service";

type Props = {
  control: any;
  register: any;
  setValue: any;
  getValues: any;
  errors: any;
  watch: any;
  currentCompany?: any;
  order?: any;
  clearErrors: any;
  receptions?: any;
  ops?: any;
  editReceptionClick?: any;
  isReadOnly?: boolean;
};

function SubordersForm({
  control,
  register,
  setValue,
  getValues,
  errors,
  watch,
  currentCompany,
  order,
  clearErrors,
  receptions,
  ops,
  editReceptionClick,
  isReadOnly,
}: Props) {
  const { fields, remove } = useFieldArray({
    control,
    name: "suborders",
  });

  const { classes } = useOrder();
  const { jclasses } = useJStyles();

  const { isAdmin, token } = useAuth();

  const { filters, setFilter } = useFilters("order-form");
  const [isSubFilterUsed, setIsSubFilterUsed] = useState(false);
  const [subordersWithEdbs, setSubordersWithEdbs] = useState<number[]>([]);

  // Use refs to track previous values and avoid unnecessary API calls
  const prevSuborderIdsRef = useRef<string>("");

  const suborderIds = useMemo(() => {
    return fields
      .map((_, index) => getValues(`suborders[${index}].id`))
      .filter((id): id is number => !!id);
  }, [fields, getValues]);

  // Extract suborders with receptions directly from the receptions prop
  // This avoids an unnecessary API call
  // Use the same logic as ButtonsList.getCurrentReception
  const subordersWithReceptions = useMemo(() => {
    if (!receptions || receptions.length === 0) {
      return [];
    }

    const uniqueSuborderRefs = new Set<string>();

    receptions.forEach((reception: any) => {
      // Look through orders in the reception
      reception.orders?.forEach((order: any) => {
        // Look through suborders in each order
        order.suborders?.forEach((suborder: any) => {
          if (suborder.reference) {
            uniqueSuborderRefs.add(suborder.reference);
          }
        });
      });
    });

    return Array.from(uniqueSuborderRefs);
  }, [receptions]);

  // Check which suborders are linked to EDBs - optimized with memoization
  useEffect(() => {
    const checkEdbs = async () => {
      if (!token) return;

      // Convert to string for comparison
      const currentIds = suborderIds.join(",");

      // Skip if IDs haven't changed
      if (currentIds === prevSuborderIdsRef.current) return;
      prevSuborderIdsRef.current = currentIds;

      if (suborderIds.length > 0) {
        const result = await checkSuborderEdbs(suborderIds, token);
        setSubordersWithEdbs(result.subordersWithEdbs || []);
      } else {
        setSubordersWithEdbs([]);
      }
    };

    checkEdbs();
  }, [suborderIds, token]);

  // Helper function to check if a suborder has receptions
  const hasSuborderReception = (suborderRef: string | undefined): boolean => {
    if (!suborderRef) return false;
    return subordersWithReceptions.includes(suborderRef);
  };

  // Helper function to check if a suborder is linked to an EDB
  const hasSuborderEdb = (suborderId: number | undefined): boolean => {
    if (!suborderId) return false;
    return subordersWithEdbs.includes(suborderId);
  };

  // Check if any suborder has receptions (for "delete all" button)
  const hasAnySuborderWithReception = (): boolean => {
    return subordersWithReceptions.length > 0;
  };

  // Check if any suborder is linked to an EDB (for "delete all" button)
  const hasAnySuborderWithEdb = (): boolean => {
    return subordersWithEdbs.length > 0;
  };

  const handleToggleAdvanced = () => {
    // si on a déjà crée une commande et une (ou aucune) ligne, mais que c'est vide
    const isCreatedButUnused =
      getValues().suborders.length === 1 &&
      getValues().suborders[0].suborderProducts.length < 2 &&
      getValues().suborders[0].suborderProducts[0].reference === "";

    if (!fields.length) {
      setValue("suborders", [
        ...getValues().suborders,
        {
          type: "stock",
          suborderProducts: [{ reference: "" }],
        },
      ]);
    } else if (isCreatedButUnused) {
      setValue("suborders", []);
    }
  };

  // TODO : change the any cast to a Project when the MR for the BL-179 ticket will be closed
  const renderDockyardOptions = (option: Dockyard) => {
    return (
      <Box
        display="flex"
        flexDirection="row"
        className={clsx(jclasses.optionsGap)}
      >
        {option?.project && (
          <span>{`[Projet: ${(option?.project as any).name}] `}</span>
        )}
        <span>{option?.name}</span>
        {option?.code && (
          <span className={clsx(jclasses.optionsDetails)}>{option?.code}</span>
        )}
      </Box>
    );
  };

  // Display suborders sorted by their reference (alphanumeric),
  // while keeping the underlying react-hook-form indices intact.
  const sortedSuborders = useMemo(() => {
    const collator = new Intl.Collator("fr", {
      numeric: true,
      sensitivity: "base",
    });
    return fields
      .map((item, index) => {
        const refValue =
          (getValues(`suborders[${index}].reference`) as string | undefined) ??
          (item as any)?.reference;
        const ref =
          typeof refValue === "string" && refValue.trim() !== ""
            ? refValue
            : undefined;
        return { item, index, ref, sortKey: ref ?? "" };
      })
      .sort((a, b) => collator.compare(a.sortKey, b.sortKey));
  }, [fields, getValues]);

  return (
    <Accordion
      style={{ marginTop: 20, boxShadow: "none" }}
      defaultExpanded={fields ? fields.length > 0 : false}
      onChange={() => handleToggleAdvanced()}
      disabled={!currentCompany}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreRounded />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div
          className={jclasses.formSectionHead}
          style={{ margin: 0, width: "100%" }}
        >
          <Text type="h3">Avancé</Text>
          <Text type="text">
            {!fields &&
              fields?.length === 0 &&
              isAdmin() &&
              "Veuillez remplir le contenu"}

            {fields && fields?.length === 0 && "Contenu global"}

            {fields?.length === 1 && "Contenu global"}

            {fields?.length > 1 && `${fields?.length} sous-commandes`}
          </Text>
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <div style={{ width: "100%" }}>
          {fields.length > 0 && (
            <div className={classes.paperFormTopHeader}>
              <div>
                <Text
                  type="text"
                  style={{ fontWeight: "bold", color: "#777777" }}
                >{`Commande n° ${
                  order?.reference ?? getValues()?.reference ?? "XXX"
                }`}</Text>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  flex: 1,
                  justifyContent: "flex-end",
                }}
              >
                {fields.length > 1 && (
                  <div style={{ marginRight: 30, width: "50%", maxWidth: 410 }}>
                    <ProductSearchInput
                      disabled={isSubFilterUsed}
                      value={filters.product}
                      onChange={(e: any) => {
                        setFilter("product", e.target.value);
                        // setProductFilter(e.target.value);
                      }}
                      mode="both"
                      label={null}
                      placeholder="Rechercher par référence ou désignation"
                    />
                  </div>
                )}
                {isAdmin() && !isReadOnly && (
                  <UIButton
                    variant="text"
                    style={{ textTransform: "none" }}
                    onClick={() => remove()}
                    className={classes.deleteButton}
                    disabled={
                      hasAnySuborderWithReception() || hasAnySuborderWithEdb()
                    }
                  >
                    <DeleteBin2LineIcon />
                  </UIButton>
                )}
              </div>
            </div>
          )}
          {sortedSuborders.map(({ item, index, ref }) => {
            const initialType = getValues(`suborders[${index}].type`);
            const currentType = watch(`suborders[${index}].type`, initialType);

            return (
              <Paper
                key={item.id}
                className={classes.paperForm}
                style={{ marginTop: 20, marginBottom: 0 }}
              >
                <div className={classes.paperFormHead}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Text type="text">
                      {fields.length === 1
                        ? "Commande"
                        : `Sous-commande n° ${
                            ref ||
                            `${
                              order?.reference ??
                              getValues()?.reference ??
                              "XXX"
                            }-${index + 1}`
                          }`}
                    </Text>
                    <ButtonsList
                      currentCompany={currentCompany}
                      ops={ops}
                      editReceptionClick={editReceptionClick}
                      order={order}
                      receptions={receptions}
                      isReadOnly={isReadOnly}
                      reference={getValues(`suborders[${index}].reference`)}
                      suborder={getValues(`suborders[${index}]`)}
                    />
                  </div>
                  {isAdmin() && !isReadOnly && (
                    <UIButton
                      variant="text"
                      style={{ textTransform: "none" }}
                      onClick={() => remove(index)}
                      disabled={
                        hasSuborderReception(
                          getValues(`suborders[${index}].reference`)
                        ) || hasSuborderEdb(getValues(`suborders[${index}].id`))
                      }
                      className={classes.deleteButton}
                    >
                      <DeleteBin2LineIcon />
                    </UIButton>
                  )}
                </div>
                <div className={classes.paperFormBody}>
                  <div className={classes.paperFormBodyTop}>
                    <div style={{ marginRight: 80 }}>
                      <FormLabel id="demo-row-radio-buttons-group-label">
                        Type :{" "}
                      </FormLabel>

                      <Controller
                        control={control}
                        name={`suborders[${index}].type`}
                        {...register(`suborders[${index}].type`)}
                        defaultValue={getValues(`suborders[${index}].type`)}
                        rules={{
                          required: "Veuillez remplir ce champ",
                        }}
                        render={({ field }) => (
                          <>
                            <RadioGroup
                              row
                              aria-labelledby="demo-row-radio-buttons-group-label"
                              {...field}
                            >
                              <FormControlLabel
                                disabled={!isAdmin() || isReadOnly}
                                value="stock"
                                control={<Radio color="primary" />}
                                label="Stock"
                              />
                              <FormControlLabel
                                disabled={!isAdmin() || isReadOnly}
                                value="dockyard"
                                control={<Radio color="primary" />}
                                label="Chantier"
                              />
                              <FormControlLabel
                                disabled={!isAdmin() || isReadOnly}
                                value="project"
                                control={<Radio color="primary" />}
                                label="Projet"
                              />
                            </RadioGroup>
                            <Text className={classes.errorField}>
                              {errors.suborders &&
                                errors.suborders[index]?.type &&
                                errors.suborders[index]?.type.message}
                            </Text>
                          </>
                        )}
                      />
                    </div>
                    {currentType === "dockyard" && (
                      <Controller
                        control={control}
                        name={`suborders[${index}].dockyard`}
                        rules={{
                          required: "Veuillez remplir ce champ",
                        }}
                        render={({
                          field: { onChange, value },
                          fieldState: { error },
                        }) => (
                          <div style={{ flex: 1 }}>
                            <DockyardSelect
                              value={value}
                              errorText={error?.message}
                              onChange={(dockyardId, dockyard) => {
                                onChange(dockyardId || null);
                                setValue(
                                  `suborders[${index}].project`,
                                  dockyard?.project?._id || null
                                );
                              }}
                              companyId={currentCompany?._id ?? currentCompany}
                              label="Sélectionner le chantier"
                              disabled={
                                !currentCompany || !isAdmin() || isReadOnly
                              }
                              renderOption={renderDockyardOptions}
                            />
                            {!currentCompany && (
                              <Text
                                style={{ fontStyle: "italic", fontSize: 10 }}
                              >
                                Veuillez sélectionner une entreprise
                              </Text>
                            )}
                          </div>
                        )}
                      />
                    )}

                    {currentType === "project" && (
                      <Controller
                        control={control}
                        name={`suborders[${index}].project`}
                        rules={{
                          required: "Veuillez remplir ce champ",
                        }}
                        render={({
                          field: { onChange, value },
                          fieldState: { error },
                        }) => (
                          <div style={{ flex: 1 }}>
                            <ProjectSelect
                              error={!!error}
                              errorText={
                                error ? "Veuillez remplir ce champ" : ""
                              }
                              label="Sélectionner le projet"
                              disabled={
                                !currentCompany || !isAdmin() || isReadOnly
                              }
                              onChange={(projectId) => {
                                onChange(projectId || null);
                                setValue(
                                  `suborders[${index}].project`,
                                  projectId || null
                                );
                              }}
                              value={value}
                              companyId={
                                (currentCompany && currentCompany._id) ??
                                currentCompany
                              }
                            />
                            {!currentCompany && (
                              <Text
                                style={{ fontStyle: "italic", fontSize: 10 }}
                              >
                                Veuillez sélectionner une entreprise
                              </Text>
                            )}
                          </div>
                        )}
                      />
                    )}
                  </div>

                  <div style={{ marginTop: 15, marginBottom: 15 }}>
                    <ProductsInSubordersForm
                      nestIndex={index}
                      {...{
                        isReadOnly,
                        control,
                        register,
                        getValues,
                        errors,
                        watch,
                        setValue,
                        currentCompany: currentCompany?._id ?? currentCompany,
                        filters,
                        setFilter,
                        order,
                        clearErrors,
                        receptions,
                        index,
                        setIsSubFilterUsed,
                        subordersWithEdbs,
                        subordersWithReceptions,
                      }}
                    />
                  </div>
                </div>
              </Paper>
            );
          })}

          {isAdmin() && !isReadOnly && (
            <Button
              color="primary"
              onClick={() => {
                setValue("suborders", [
                  ...getValues().suborders,
                  {
                    type: "stock",
                    suborderProducts: [{ reference: "" }],
                  },
                ]);
              }}
              style={{ marginTop: 20 }}
              // disabled={order && order?.receptions?.length > 0}
            >
              <Addicon />
              Ajouter une {fields?.length > 0 && "sous-"}
              commande
            </Button>
          )}
        </div>
      </AccordionDetails>
    </Accordion>
  );
}

export default SubordersForm;
